import { useState } from "react";
import { Link } from "react-router-dom";
import AdSlot from "../../../components/base/AdSlot";
import type { Quiz, QuizResult } from "../../../mocks/quizzes";

interface Props {
  quiz: Quiz;
  result: QuizResult;
  onRetake: () => void;
}

export default function QuizResultScreen({ quiz, result, onRetake }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center px-4 py-16">
      {/* ── Ad: Leaderboard at very top of result page ── */}
      <div className="w-full max-w-2xl mb-8 flex justify-center">
        <div className="hidden md:block">
          <AdSlot size="leaderboard" />
        </div>
        <div className="md:hidden w-full flex justify-center">
          <AdSlot size="mobile-banner" />
        </div>
      </div>

      <div className="w-full max-w-2xl text-center">
        {/* Celebration */}
        <div className="mb-6">
          <div className="text-7xl mb-2 animate-bounce inline-block">
            {result.emoji}
          </div>
          <p
            className="text-xs font-bold uppercase tracking-widest text-[#FF6B6B] mb-2"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Your Result Is...
          </p>
          <h1
            className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            {result.title}
          </h1>
        </div>

        {/* Result Image */}
        <div className="w-full max-w-lg mx-auto h-60 md:h-72 rounded-3xl overflow-hidden mb-6">
          <img
            src={result.image}
            alt={result.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Result Description */}
        <div className="bg-white rounded-3xl p-6 md:p-8 mb-6 text-left">
          <p className="text-gray-700 text-sm md:text-base leading-relaxed">
            {result.description}
          </p>
        </div>

        {/* ── Ad: Rectangle between description and share buttons ── */}
        <div className="mb-6 flex justify-center">
          <AdSlot size="large-rectangle" />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-6 py-3 bg-[#FF6B6B] text-white font-bold rounded-full hover:bg-[#ff5252] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={copied ? "ri-checkbox-circle-line" : "ri-share-forward-2-line"} />
            {copied ? "Link Copied!" : "Share Result"}
          </button>
          <button
            onClick={onRetake}
            className="flex items-center gap-2 px-6 py-3 border-2 border-gray-200 text-gray-700 font-bold rounded-full hover:border-[#FF6B6B] hover:text-[#FF6B6B] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            Retake Quiz
          </button>
        </div>

        {/* Other Results Teaser */}
        <div className="bg-orange-50 rounded-2xl p-5 mb-8">
          <p className="text-[#FF6B6B] font-bold text-sm mb-3">
            Curious about other results? 👀
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {quiz.results.filter((r) => r.key !== result.key).map((r) => (
              <span
                key={r.key}
                className="px-3 py-1.5 bg-white text-gray-600 text-xs font-semibold rounded-full border border-orange-100"
              >
                {r.emoji} {r.title}
              </span>
            ))}
          </div>
        </div>

        {/* ── Ad: Leaderboard before "browse more quizzes" CTA ── */}
        <div className="mb-8 flex justify-center">
          <div className="hidden md:block">
            <AdSlot size="leaderboard" />
          </div>
          <div className="md:hidden w-full flex justify-center">
            <AdSlot size="large-rectangle" />
          </div>
        </div>

        {/* More Quizzes */}
        <div className="text-center">
          <p className="text-gray-500 text-sm mb-4">Want more? Try these next! ✨</p>
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white font-extrabold rounded-full hover:scale-105 transition-transform cursor-pointer whitespace-nowrap"
          >
            Browse All Quizzes
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>

      {/* ── Ad: Sticky bottom banner (mobile only) ── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 flex items-center justify-center py-1">
        <AdSlot size="sticky-mobile" />
      </div>
    </div>
  );
}
